// @flow
import React, { useState, useEffect } from 'react';
import { FinectIsoPlus } from '@finect/tabular-components/FinectIsoPlus';
import { PlusLogo } from '@finect/tabular-components/PlusLogo';
import { DateLabel } from '@finect/tabular-components/DateLabel';
import { Heart } from '@finect/tabular-icons/heart';
import { Link } from '@finect/tabular-icons/link';
import { Video } from '@finect/tabular-components/Videos';
import { getArticleRel } from '@finect/tabular-components/Articles';
// import { EntityCard } from '@finect/tabular-components/EntityCard';
import { Image } from '@finect/tabular-components/Image';
import { Flex } from '../components/Flex';
import { Topic, Topics } from '../components/Topic';
import { Title6 } from '../components/Headings';
import { getArticlePreviewText } from '../../../utils/articles';
import { SITE_TIME_ZONE } from '../../../utils/siteTimeZone';
import { Action } from '../components/Toolbar/Action';
import {
  VideoContent,
  ArticleContent,
  ArticlePlusInfo,
  ArticlePlusBInfo,
  Small,
  External,
  PlusImage
} from '../partials';
import { getCommonProps } from './utils';

const renderTags = (tags) => {
  // plus tags first
  const plusTags = tags.filter(tag => tag.id >= 50100 && tag.id <= 50110);
  const otherTags = tags.filter(tag => !(tag.id >= 50100 && tag.id <= 50110));

  return [...plusTags, ...otherTags].map((tag) => {
    const plus2 = tag.id >= 50100 && tag.id <= 50110;

    if (plus2) {
      return (
        <Topic title={tag.name} plus2={plus2} key={tag.id}>
          <FinectIsoPlus color="#fff" size={14} />
          <PlusLogo color="#fff" size={26} />
        </Topic>
      );
    }

    return (
      <Topic key={tag.id} plus href={`/temas/${tag.alias ? tag.alias : tag.url}`} title={tag.name} followed={true}>
        {tag.name}
      </Topic>
    );
  });
};

type Props = {
  variant?: 'a' | 'b',
  article: Object,
  onSubscribe: Function,
  userSubscriptions: Object,
  userLikes: Object,
  withAuthor?: boolean,
  withText?: boolean,
  noMarginTop?: boolean,
  noBorderTop?: boolean,
  imageHD?: boolean
};

export const ArticlePlusListCard = ({
  variant = 'a',
  article,
  onSubscribe = null,
  userSubscriptions = {},
  userLikes = {},
  withAuthor = true,
  withText = false,
  noMarginTop = false,
  noBorderTop = false,
  imageHD = false
}: Props) => {
  const { title, date, externalUrl, externalPath, link, image, author, authorContent, tags, linkProps, video } =
    getCommonProps(article, userSubscriptions, true, imageHD);

  const userLikesArticle = userLikes.articles && userLikes.articles.find(a => a === article.id);
  const tagsContent = tags && tags.length > 0 ? <Topics>{renderTags(tags)}</Topics> : <div />;

  const [maxPhone, setMaxPhone] = useState(false);
  const [maxTablet, setMaxTablet] = useState(false);
  useEffect(() => {
    setMaxPhone(window.innerWidth < 500);
    setMaxTablet(window.innerWidth < 800);
  }, []);
  const VariantContainer = variant === 'a' ? ArticlePlusInfo : ArticlePlusBInfo;
  return (
    <ArticleContent noMarginTop={noMarginTop} noBorderTop={noBorderTop} className="article-plus-list-card">
      <VariantContainer hasImage={Boolean(image)} hasVideo={Boolean(['video'].includes(article.type) && video)}>
        <div>
          {['video'].includes(article.type) && video ? (
            <VideoContent>
              {/* Miniatura enlaza a la página del artículo (no abre modal). */}
              <Video video={video} href={link} rel={getArticleRel(article)} title={title} />
            </VideoContent>
          ) :
            image && (
              <PlusImage alt={title} href={link} {...linkProps}>
                <div className="overlay" />
                <Image alt={title} src={image} height={240} />
                <div className="tags-title-mobile">
                  <div className="tags-fav-mobile">
                    {variant === 'a' && tagsContent}
                    {variant === 'a' && onSubscribe && (
                      <Action
                        inline
                        plus
                        color="primary"
                        done={userLikesArticle}
                        onClick={() => onSubscribe(article.id)}
                      >
                        <Heart />
                      </Action>
                    )}
                  </div>

                  <Title6 as="h3">
                    <a
                      href={article.type !== 'podcast' && externalUrl ? externalUrl : link}
                      title={title}
                      {...linkProps}
                    >
                      {title}
                    </a>
                  </Title6>
                </div>
              </PlusImage>
            )
          }
        </div>

        <div>
          <div className="tags-title-desktop">
            <div className="tags-fav-desktop">
              {variant === 'a' && tagsContent}
              {variant === 'a' && onSubscribe && (
                <Action inline plus color="primary" done={userLikesArticle} onClick={() => onSubscribe(article.id)}>
                  <Heart />
                </Action>
              )}
            </div>

            <Title6 as="h3">
              <a href={article.type !== 'podcast' && externalUrl ? externalUrl : link} title={title} {...linkProps}>
                {title}
              </a>
            </Title6>
          </div>

          {withText && (
            <p className="text">
              {article.metadata.description
                ? article.metadata.description
                : article.body.text
                  ? getArticlePreviewText(
                    article.body.text,
                    variant === 'a'
                      ? title.length > 100
                        ? maxTablet
                          ? 100
                          : 380
                        : maxTablet
                          ? 160
                          : 400
                      : title.length > 100
                        ? 140
                        : 160
                  )
                  : null}
            </p>
          )}

          <Flex align="center" className="author-date">
            {author && withAuthor && authorContent}
            <div className="date-external">
              {externalUrl && (
                <Small>
                  <External href={externalUrl} rel="nofollow" target="_blank">
                    <Link size="16" strokeWidth="2.2" />
                    <span>{externalPath ? externalPath[0] : ''}</span>
                  </External>
                </Small>
              )}
              <Small withLeftMargin={Boolean(author && withAuthor)}>
                <DateLabel date={new Date(date)} format={maxPhone ? 'LL' : 'LLL'} timeZone={SITE_TIME_ZONE} />
              </Small>
            </div>
          </Flex>
        </div>
      </VariantContainer>
    </ArticleContent>
  );
};
